import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import {
    Box,
    Stack,
    Typography,
    Card,
    Grid,
    CardContent,
} from "@mui/material";
import { useGridApiRef, getGridStringOperators } from "@mui/x-data-grid";
import Chart from "../../components/Chart"
import NodataImg from "../../assets/images/noData.png";
import { useDispatch, useSelector } from "react-redux";
import { analyticsTableApi } from "../../store/analytics/analyticsList";
import { ThemeContext } from '../../context/ThemeContext';
import dayjs from "dayjs";
import CustomGridToolbar from "../../components/CustomGridToolbar";
import apiRoutes from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import DataGridComponent from "../../components/DataGridComponent";

const columns = [

    {
        field: "id",
        headerName: "Sno.",
        width: 150,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
    },
    {
        field: "fullname",
        headerName: "Full Name",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
    },
    {
        field: "email",
        headerName: "Email",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
    },
    {
        field: "phone_number",
        headerName: "Phone Number",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
    },
    {
        field: "state",
        headerName: "State Name",
        width: 450,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },

    {
        field: "city",
        headerName: "Region Name",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },


    {
        field: "language",
        headerName: "Language",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },
    {
        field: "gender",
        headerName: "Gender",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },
    {
        field: "pincode",
        headerName: "Pincode",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },
    {
        field: "school_name",
        headerName: "School Name",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
    },
    {
        field: "school_type",
        headerName: "School Type",
        width: 200,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals',
        ),
        sortable: false,
    },
    {
        field: "last_login",
        headerName: "Last login",
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{data?.row?.last_login && dayjs(data?.row?.last_login).format("YYYY-MM-DD HH:mm:ss")}</Typography>
        </>)
    },
    {
        field: "subjects",
        headerName: "Subjects",
        width: 200,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{Array.isArray(data?.row?.subjects) && data?.row?.subjects.toString()}</Typography>
        </>),
        filterable: false,
        sortable: false,
    },
    {
        field: "classes",
        headerName: "Classes",
        width: 200,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{Array.isArray(data?.row?.classes) && data?.row?.classes.toString()}</Typography>
        </>),
        filterable: false,
        sortable: false,
    },
    {
        field: "user_type",
        headerName: "User type",
        width: 200,
        filterable: false,
        sortable: false,
    },
    {
        field: "courses_enrolled",
        headerName: "Courses enrolled",
        width: 200,
        filterable: false,
        sortable: false,
    },
    {
        field: "courses_completed",
        headerName: "Courses completed",
        width: 200,
        filterable: false,
        sortable: false,
    },
    {
        field: "created_at",
        sortable: true,
        filterable: false,
        headerName: "Registration Date",
        width: 150,
    },
];

const timeSpentColumns = [
    {
        field: "module",
        headerName: "Module",
        // width: 200,
        flex: 1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

        renderCell: ({ row }) => {
            const formatted =
                row?.module
                    ?.replace(/_/g, " ")
                    ?.toLowerCase()
                    ?.replace(/^\w/, c => c.toUpperCase());

            return <Typography>{formatted}</Typography>;
        },
    },
    {
        field: "user_count",
        headerName: "Total users",
        // width: 200,
        flex: 1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

    },
    {
        field: "spend_time_avg",
        headerName: "Average time spent",
        // width: 200,
        flex: 1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

    },

]

export default function HealthCheck({ graphData, region, state, loading, type, monthDaily, startDate, endDate, isMaskingEnabled }) {
    const [activevsavgtimespent, setActiveVsAvgTimeSpent] = useState({ l1: true, l2: true });
    const [teacherRegister, setTeacherRegistration] = useState({ l1: true, l2: true, l3: true, l4: true });
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const [isDownload, setIsDownload] = useState(false)
    const analyticsTable = useSelector((state) => state?.analyticsTable);
    const { result: tableResult, loading: tableLoading, total } = analyticsTable;
    const [queryOptions, setQueryOptions] = useState({});
    const { paginationModel, setPaginationModel } = useContext(ThemeContext)

    const onFilterChange = useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });
    }, []);
    const transformedGraphData = useMemo(() => {
        // Skip computation when data is not yet loaded or was cleared by a pending API call
        if (!graphData || (!graphData?.analytics_data?.length && !graphData?.registration_data)) {
            return [];
        }

        const isDaily = type === 'daily' || !!(startDate && endDate);
        const aggregatedData = {};

        const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        // --- Helper: get chronological months (last 12 months ending at current month) ---
        const getChronologicalMonths = () => {
            const list = [];
            const now = new Date();
            for (let i = 11; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                list.push(d.toLocaleString('default', { month: 'long' }).slice(0, 3));
            }
            return list;
        };

        const chronologicalMonths = getChronologicalMonths();

        // --- Helper: create an empty data entry ---
        const createEmptyEntry = (name) => ({
            name,
            "Acquisition": 0,
            "Active user": 0,
            "Average Time Spent": 0,
            "Teachers": 0,
            "Educators": 0,
            "Parents": 0,
            amt: 0,
            graphData: [],
            _sortKey: name, // used for sorting; will be overridden for daily entries
        });

        // --- Helper: format an ISO date key for display on the chart axis ---
        const formatDateLabel = (isoKey) => {
            const d = new Date(isoKey + 'T00:00:00');
            if (isNaN(d.getTime())) return isoKey;
            const day = d.getDate();
            const mon = d.toLocaleString('default', { month: 'long' }).slice(0, 3);
            return `${day} ${mon}`;
        };

        // --- Helper: convert a date string to an internal key ---
        // Monthly mode → 3-letter month (e.g., "Jan")
        // Daily mode → ISO string YYYY-MM-DD (e.g., "2026-05-15")
        const getDateKey = (dateStr) => {
            if (!dateStr) return null;

            // Monthly mode: if the string is already a 3-letter month, use it directly
            if (!isDaily && MONTHS.includes(dateStr)) {
                return dateStr;
            }

            // Daily mode: if the string is a bare month name, skip it (stale monthly data)
            if (isDaily && MONTHS.includes(dateStr)) {
                return null;
            }

            const d = new Date(dateStr);
            if (isNaN(d.getTime())) {
                return null;
            }

            if (isDaily) {
                // Return ISO date key for reliable sorting
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }

            // Monthly: always use 3-letter month abbreviation
            return d.toLocaleString('default', { month: 'long' }).slice(0, 3);
        };

        // --- Helper: ensure a dateKey bucket exists ---
        const ensureBucket = (key) => {
            if (!key) return;
            if (!aggregatedData[key]) {
                aggregatedData[key] = createEmptyEntry(key);
                aggregatedData[key]._sortKey = key;
            }
        };

        // --- 1. Aggregate analytics_data ---
        graphData?.analytics_data?.forEach((entry) => {
            const dateKey = getDateKey(entry.date);
            if (!dateKey) return;
            ensureBucket(dateKey);

            const bucket = aggregatedData[dateKey];
            bucket["Active user"] += parseInt(entry.activeUsers, 10) || 0;

            const duration = Math.round((parseFloat(entry.averageSessionDuration) || 0) * 100) / 100;
            bucket["Average Time Spent"] += duration;
            bucket.amt += duration;
            bucket.graphData.push(entry);
        });

        // --- 2. Fill empty gaps so the chart axis is continuous ---
        if (isDaily) {
            // For daily: fill every day in the date range (or auto-detect from data)
            let rangeStart = startDate ? new Date(startDate) : null;
            let rangeEnd = endDate ? new Date(endDate) : null;

            if ((!rangeStart || isNaN(rangeStart.getTime())) && graphData?.analytics_data?.length) {
                const dates = graphData.analytics_data.map(d => new Date(d.date)).filter(d => !isNaN(d.getTime()));
                if (dates.length > 0) {
                    rangeStart = new Date(Math.min(...dates));
                    rangeEnd = new Date(Math.max(...dates));
                }
            }

            if (rangeStart && rangeEnd && !isNaN(rangeStart.getTime()) && !isNaN(rangeEnd.getTime())) {
                const cursor = new Date(rangeStart);
                cursor.setHours(0, 0, 0, 0);
                const end = new Date(rangeEnd);
                end.setHours(0, 0, 0, 0);
                while (cursor <= end) {
                    const dateKey = getDateKey(cursor.toISOString());
                    if (dateKey) ensureBucket(dateKey);
                    cursor.setDate(cursor.getDate() + 1);
                }
            }
        } else {
            // For monthly: fill based on the chronological months list
            chronologicalMonths.forEach((monthKey) => {
                ensureBucket(monthKey);
            });
        }

        // --- 3. Aggregate registration_data using a single generic loop ---
        const registrationFieldMap = {
            teachers: "Teachers",
            parents: "Parents",
            educators: "Educators",
            total_user: "Acquisition"
        };

        const registrationData = graphData?.registration_data;
        if (registrationData) {
            Object.entries(registrationFieldMap).forEach(([apiKey, fieldName]) => {
                registrationData[apiKey]?.forEach((obj) => {
                    const dateStr = obj.day || obj.month;
                    if (!dateStr) return;

                    let dateKey;
                    if (isDaily) {
                        if (!obj.day) return;
                        dateKey = getDateKey(obj.day);
                    } else {
                        // Monthly: obj.day is ISO date, obj.month might be full month name
                        if (obj.day) {
                            dateKey = getDateKey(obj.day);
                        } else if (obj.month) {
                            const d = new Date(obj.month);
                            if (!isNaN(d.getTime())) {
                                dateKey = d.toLocaleString('default', { month: 'long' }).slice(0, 3);
                            } else {
                                dateKey = obj.month.slice(0, 3);
                            }
                        }
                    }

                    if (!dateKey) return;

                    ensureBucket(dateKey);
                    aggregatedData[dateKey][fieldName] += parseInt(obj.count, 10) || 0;
                });
            });
        }

        // --- 4. Final rounding, labeling & sorting ---
        const mergedData = Object.values(aggregatedData).map((item) => ({
            ...item,
            // For daily: replace ISO key (e.g., "2026-05-15") with human-readable label (e.g., "15 May")
            name: isDaily ? formatDateLabel(item.name) : item.name,
            "Average Time Spent": Math.round(item["Average Time Spent"] * 100) / 100
        }));

        if (isDaily) {
            // Sort by the internal _sortKey (ISO date string) for correct chronological order
            return mergedData.sort((a, b) => a._sortKey.localeCompare(b._sortKey));
        }

        // Monthly: sort by chronological month order
        return mergedData.sort((a, b) => chronologicalMonths.indexOf(a.name) - chronologicalMonths.indexOf(b.name));
    }, [graphData, type, startDate, endDate]);
    const toggleTeacherRegistration = useCallback((key) => {
        setTeacherRegistration(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const toggleActiveVsAvgTimeSpent = useCallback((key) => {
        setActiveVsAvgTimeSpent(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const getRowSpacing = useCallback((params) => {
        return {
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
        };
    }, []);
    const handleSortModelChange = useCallback((sortModel) => {
        setQueryOptions({ sortModel: [...sortModel] });
    }, []);
    useEffect(() => {
        // console.log("isMaskingEnabled",isMaskingEnabled);
        const filterModel = queryOptions?.filterModel;
        const items = filterModel?.items;
        const firstItem = items && items.length > 0 ? items[0] : {};
        const quickFilterValues = filterModel?.quickFilterValues;
        const sortModel = queryOptions?.sortModel;
        let query
        if (sortModel?.length > 0) {
            query = {
                page: paginationModel?.page + 1,
                page_size: paginationModel?.pageSize,
                sort_order: sortModel[0].sort || "desc",
                field: firstItem.field || '',
                city: region,
                state: state,
                startDate: startDate,
                endDate: endDate,
                frequency: (startDate || endDate) ? 'daily' : monthDaily,
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        } else {
            query = {
                page: paginationModel?.page + 1,
                page_size: paginationModel?.pageSize,
                field: firstItem.field || '',
                city: region,
                sort_order: "desc",
                state: state,
                startDate: startDate,
                endDate: endDate,
                frequency: (startDate || endDate) ? 'daily' : monthDaily,
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        }
        dispatch(analyticsTableApi(query));
    }, [dispatch, queryOptions, paginationModel, region, state, startDate, endDate, isMaskingEnabled]);

    // console.log("graphData", graphData?.time_spent);
    return (
        <>
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center" justifyContent={"space-between"}>
                <Typography variant="h3">Health Check</Typography>
                {/* <Tooltip title="Download Report">
                {isDownload ? (<CircularProgress />) : (<Button type="button" onClick={() => { handleApiCall() }}><FaDownload size={28} /></Button>)}
            </Tooltip> */}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Grid container spacing={1}>
                    {graphData && ["total_registered_users", "new_users"].map((key) => (
                        <Grid item xs={6} sm={6} md={6} lg={3} mb={1} key={key}>
                            <Box
                                className="customCard1"
                                sx={{
                                    transition: "none !important",
                                    "&:hover": {
                                        transform: "none !important",
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Stack>
                                        <Typography sx={{ textTransform: "capitalize" }}>
                                            {key === "new_users" ? "Acquisition" : key.replaceAll("_", " ")}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={600}>
                                            {graphData[key] ?? 0}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Grid container spacing={1}>
                    <Grid item xl={3} lg={3} sm={12} xs={12}>
                        <Card sx={{
                            minHeight: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: teacherRegister.l1 && "antiquewhite"
                        }}
                            onClick={() => toggleTeacherRegistration('l1')}
                        >
                            <CardContent>
                                <Typography gutterBottom variant="h4" component="div">Teachers</Typography>
                            </CardContent>
                        </Card>

                    </Grid>
                    <Grid item xl={3} lg={3} sm={12} xs={12}>
                        <Card sx={{
                            minHeight: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: teacherRegister.l2 && "antiquewhite"
                        }}
                            onClick={() => toggleTeacherRegistration('l2')}
                        >
                            <CardContent>
                                <Typography gutterBottom variant="h4" component="div">Acquisition</Typography>
                            </CardContent>
                        </Card>

                    </Grid>
                    <Grid item xl={3} lg={3} sm={12} xs={12}>
                        <Card sx={{
                            minHeight: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: teacherRegister.l3 && "antiquewhite"
                        }}
                            onClick={() => toggleTeacherRegistration('l3')}>
                            <CardContent>
                                <Typography gutterBottom variant="h4" component="div">Parents</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xl={3} lg={3} sm={12} xs={12}>
                        <Card sx={{
                            minHeight: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: teacherRegister.l4 && "antiquewhite"
                        }}
                            onClick={() => toggleTeacherRegistration('l4')}>
                            <CardContent>
                                <Typography gutterBottom variant="h4" component="div">Educators</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Chart type={"line"} height={500} data={transformedGraphData} module={"teacherRegister"} lineState={teacherRegister} />
            </Stack>
            {/* <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center"> */}
            {/* teacher user parent educator  */}
            {/* <DataGridComponent
                    loading={tableLoading}
                    rowCount={total}
                    pageSize={paginationModel?.pageSize}
                    onPageSizeChange={(newPageSize) => {
                        setPaginationModel({
                            ...paginationModel,
                            pageSize: newPageSize,
                        })
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        height: 500
                    }}
                    getRowId={(row) => row.id}
                    rows={tableResult}
                    getRowSpacing={getRowSpacing}
                    columns={columns}
                    onSortModelChange={handleSortModelChange}
                    onFilterModelChange={onFilterChange}
                    slots={{
                        noRowsOverlay: () => (
                            <Box className="noData">
                                <img src={NodataImg} alt="no data found" />
                            </Box>
                        ),
                        toolbar: (props) =>
                        (CustomGridToolbar({
                            ...props, moduleType: "healthCheck", queryOptions: queryOptions, paginationModel: paginationModel, isDownload: isDownload, setIsDownload: setIsDownload, apiUrl: apiRoutes.userList,
                            region: region,
                            state: state,
                            startDate: startDate,
                            endDate: endDate
                        })),
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                /> */}
            {/* </Stack> */}
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
                {/* time spent table  */}
                <DataGridComponent
                    loading={tableLoading}
                    pagination={false}
                    hideFooterPagination
                    sx={{
                        height: 500
                    }}
                    getRowId={(row) => row.module}
                    rows={graphData?.time_spent || []}
                    getRowSpacing={getRowSpacing}
                    columns={timeSpentColumns}
                    onSortModelChange={handleSortModelChange}
                    onFilterModelChange={onFilterChange}
                    slots={{
                        noRowsOverlay: () => (
                            <Box className="noData">
                                <img src={NodataImg} alt="no data found" />
                            </Box>
                        ),
                        toolbar: (props) =>
                        (CustomGridToolbar({
                            ...props, moduleType: "healthCheck",
                            isDownload: isDownload,
                            setIsDownload: setIsDownload,
                            apiUrl: apiRoutes.healthcheckReportCsvExport,
                            analytics_type: "health_check",
                            hasfilter: false,
                            hasSearch: false,
                            startDate: startDate,
                            endDate: endDate,
                            region: region,
                            state: state,
                            additionalQueryParams: {
                                frequency: (startDate || endDate) ? 'daily' : monthDaily,
                            }
                        })),
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                />
            </Stack>
            <BackdropUI open={isDownload} />
        </>)
}